import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GuideMangoPayAccount } from '../../models/account';
import { KycDocument } from '../../models/KycDocument';
import { BankAccount } from '../../models/BankAccount';
import { Payment } from '../../models/payment';
import { RegisterDirectPayment } from '../../models/register-payment';
import { Coupon } from '../../models/coupon';
import { PaymentIntent, PaymentIntentRequest } from '../../models/paymentIntent';
import { GiftCard } from '../../models/gift-card';
import { BaseApiService } from '@core/http/base.api.service';
import { ConfigService } from '@core/services/config.service';

@Injectable()
export class PaymentApiService extends BaseApiService {
  constructor(public http: HttpClient, private configService: ConfigService) {
    super(http);
  }

  private get baseUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.payment}/api/account`;
  }

  private get paymentBaseUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.payment}/api/payment`;
  }

  private get couponBaseUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.payment}/api/coupon`;
  }

  private get giftCardBaseUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.payment}/api/giftcard`;
  }

  public registerPayment(registerPayment: Partial<RegisterDirectPayment>): Observable<Payment> {
    return this.http.post<Payment>(`${this.paymentBaseUrl}/direct`, registerPayment);
  }

  public completePayment(transectionId: string): Observable<Payment> {
    return this.http.put<Payment>(`${this.paymentBaseUrl}/direct/${transectionId}`, transectionId);
  }

  public accountRegistration(account: GuideMangoPayAccount): Observable<GuideMangoPayAccount> {
    return this.http.post<GuideMangoPayAccount>(`${this.baseUrl}`, account);
  }

  public accountUpdate(id: string, account: Partial<GuideMangoPayAccount>): Observable<GuideMangoPayAccount> {
    return this.http.put<GuideMangoPayAccount>(`${this.baseUrl}/${id}`, account);
  }

  public getAccount(): Observable<GuideMangoPayAccount[]> {
    return this.http.get<GuideMangoPayAccount[]>(`${this.baseUrl}/me`);
  }

  public getKycDocuments(accountId?: string): Observable<KycDocument[]> {
    return this.http.get<KycDocument[]>(`${this.baseUrl}/${accountId}/document`);
  }

  public uploadKYC(accountId: string, documentId: string, files: File[], kycType: string): Observable<void> {
    const param = new HttpParams().append('documentType', kycType);

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    return this.http.post<void>(`${this.baseUrl}/${accountId}/document/${documentId}`, formData, { params: param });
  }

  public submitKycDocument(accountId: string, documentId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${accountId}/document/${documentId}/submit`, null);
  }

  public registerBankAccount(accountId: string, bankAccount: BankAccount): Observable<BankAccount> {
    return this.http.post<BankAccount>(`${this.baseUrl}/${accountId}/bankaccount`, bankAccount);
  }

  // tslint:disable-next-line: max-line-length
  public updateBankAccount(
    accountId: string,
    bankAccountId: string,
    bankAccount: Partial<BankAccount>
  ): Observable<BankAccount> {
    return this.http.put<BankAccount>(`${this.baseUrl}/${accountId}/bankaccount/${bankAccountId}`, bankAccount);
  }

  public getBankAccount(accountId?: string): Observable<BankAccount[]> {
    return this.http.get<BankAccount[]>(`${this.baseUrl}/${accountId}/bankaccount`);
  }

  public getBankAccountById(accountId: string, bankAccountId: string): Observable<BankAccount> {
    return this.http.get<BankAccount>(`${this.baseUrl}/${accountId}/bankaccount/${bankAccountId}`);
  }

  public discount(coupon: Coupon): Observable<Coupon> {
    return this.http.post<Coupon>(`${this.paymentBaseUrl}/discount`, coupon);
  }

  public getDiscountedPrice(bookingId: string, couponCode: string): Observable<number> {
    const params = new HttpParams()
      .append('bookingId', bookingId)
      .append('couponCode', couponCode)
    return this.http.get<number>(`${this.paymentBaseUrl}/discountedPrice`, { params });
  }

  public directPayment(paymentIntentParams: PaymentIntentRequest, isAgreedAgeRestriction: boolean): Observable<PaymentIntent> {
    const params = new HttpParams().append('isAgreedAgeRestriction', isAgreedAgeRestriction)
    return this.http.post<PaymentIntent>(`${this.paymentBaseUrl}/direct`, paymentIntentParams, { params });
  }

  public directPaymentTransaction(transactionId: string): Observable<PaymentIntent> {
    return this.http.put<PaymentIntent>(`${this.paymentBaseUrl}/direct/${transactionId}`, {});
  }

  public checkGiftCardStatus(giftCode: string, bookingId: string): Observable<any> {
    const params = new HttpParams().append('bookingId', bookingId)
    return this.http.get<any>(`${this.giftCardBaseUrl}/CheckGiftCardStatus/${giftCode}`, { params });
  }

  public giftCardPayment(giftCard: GiftCard): Observable<PaymentIntent> {
    return this.http.post<PaymentIntent>(`${this.paymentBaseUrl}/giftcard`, giftCard);
  }
}
